import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { campaignService } from "../api/services/campaignService";
import type {
  BrandScreenMap,
  Campaign,
  CampaignFilters,
  CampaignPlacement,
  CampaignPlacementsResponse,
  CampaignSummary,
  CampaignsResponse,
} from "../schemas/campaignSchema";
import { DEFAULT_PAGE_SIZE } from "../constants";
import {
  demoBrandCampaignSummary,
  demoBrandScreenMap,
  isBrandDemoData,
} from "../mocks/brandCampaignDemo";

/**
 * The signed-in brand's campaigns.
 *
 * Read-only throughout: the tenant owns the creatives and the scheduling, so a
 * brand watches its campaigns rather than editing them. Scope comes from the
 * auth context on the server, so none of these hooks take a brand id.
 */
export type BrandCampaignFilters = Omit<CampaignFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useBrandCampaignsInfinite = (
  filters: BrandCampaignFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const query = useInfiniteQuery<CampaignsResponse, Error>({
    queryKey: ["brand-campaigns", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      campaignService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const campaigns: Campaign[] =
    query.data?.pages.flatMap((p) => p.campaigns) ?? [];

  return {
    campaigns,
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};

/** The screens one campaign runs on. Disabled until a campaign is chosen. */
export const useBrandCampaignScreensInfinite = (
  creativeId: string | undefined,
  filters: BrandCampaignFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const query = useInfiniteQuery<CampaignPlacementsResponse, Error>({
    queryKey: [
      "brand-campaign-screens",
      creativeId,
      { ...rest, pageSize },
    ],
    queryFn: ({ pageParam }) =>
      campaignService.getScreens({
        creativeId: creativeId!,
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
    enabled: !!creativeId,
  });

  const placements: CampaignPlacement[] =
    query.data?.pages.flatMap((p) => p.placements) ?? [];

  return {
    placements,
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};

/**
 * Every screen carrying one of the brand's campaigns, unfiltered.
 *
 * Status filtering happens in the browser (see utils/coverageMap) so the
 * Live / Upcoming / Expired buttons, their counts and the pins are all derived
 * from this one payload.
 */
export const useBrandScreenMap = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const query = useQuery<BrandScreenMap, Error>({
    queryKey: ["brand-screen-map", isBrandDemoData()],
    queryFn: () =>
      isBrandDemoData()
        ? Promise.resolve(demoBrandScreenMap())
        : campaignService.getScreenMap(),
    enabled,
  });

  return {
    screens: query.data?.screens ?? [],
    truncated: query.data?.truncated ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};

export const useBrandCampaignSummary = () => {
  const query = useQuery<CampaignSummary, Error>({
    queryKey: ["brand-campaign-summary", isBrandDemoData()],
    queryFn: () =>
      isBrandDemoData()
        ? Promise.resolve(demoBrandCampaignSummary())
        : campaignService.getSummary(),
  });

  return {
    summary: query.data,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
